import React, { FunctionComponent, useEffect, useState } from 'react';

import axios from 'axios';
import { get, has, map } from 'lodash';
import { useTranslation } from 'react-i18next';

import { useProfile } from 'auth/Auth';
import { MessageActionType, useMessage } from 'common/message-bar/MessageBar';
import { Route } from 'core/util/Route';
import { route } from 'core/util/Routing';
import {
    TicketReloadModalActionType,
    useTicketReloadModalDispatch,
} from 'tickets/actions2/common/components/TicketReloadModal';

export enum Status {
    OK = 200, // Successful,
    Error = 400, // Failed
    Unauthorized = 401, // Not Autenticated (Session expired)
    Forbidden = 403, // Not Authorized (No action)
    NotFound = 404, // Invalid endpoint
    InternalError = 500, // Hard Failed
}

// This is a component to be able to reference hooks
export const HttpInterceptor: FunctionComponent = ({ children }) => {
    const [isInterceptorRegistered, setInterceptorRegistered] = useState<boolean>(false);
    const [{ profile }] = useProfile();
    const [, dispatch] = useMessage();
    const dispatchTicketReloadModal = useTicketReloadModalDispatch();
    const { t } = useTranslation(['auth']);

    useEffect(() => {
        axios.interceptors.request.use(
            (config) => {
                dispatch({
                    type: MessageActionType.Hide,
                });

                config.headers.locale = profile?.user.locale; // eslint-disable-line no-param-reassign

                const selectedTicket = JSON.parse(localStorage.getItem('selectedTicket') || '{}');
                const { agencyID = '' } = selectedTicket || {};

                if (agencyID) {
                    config.headers['x-agency-id'] = agencyID; // eslint-disable-line no-param-reassign
                } else if (profile?.agency) {
                    config.headers['x-agency-id'] = profile?.agency?.id; // eslint-disable-line no-param-reassign
                } else if (profile?.tenant) {
                    config.headers['x-agency-id'] = profile?.tenant?.agencyID; // eslint-disable-line no-param-reassign
                }

                config.headers['x-tenant-id'] = profile?.tenant?.id; // eslint-disable-line no-param-reassign

                return config;
            },
            (error) => {
                return Promise.reject(error);
            },
        );

        axios.interceptors.response.use(
            (response) => {
                return response;
            },
            (error) => {
                if (has(error, 'response.config.url') && /^\/api/.test(error.response.config.url)) {
                    let detailErrors = error?.response?.data?.error?.detail || [];
                    const errorMessage = get(error, 'response.data.error.message');

                    if (has(detailErrors, 'error')) {
                        detailErrors = detailErrors.error;
                    }
                    const fieldMapString = map(detailErrors, 'field').join('');
                    const codeMapString = map(detailErrors, 'code').join('');

                    /* eslint-disable default-case */
                    switch (error.response.status) {
                        case Status.Unauthorized:
                            window.location.href = route(Route.Auth);
                            break;

                        case Status.Error:
                        case Status.Forbidden:
                            if (
                                detailErrors.length &&
                                fieldMapString.includes('rem') &&
                                codeMapString.includes('error.unauthorized')
                            ) {
                                dispatch({
                                    type: MessageActionType.ShowError,
                                    text: detailErrors[0].message,
                                });
                            } else if (fieldMapString === 'v') {
                                const payload = JSON.parse(error.config.data);

                                if (payload.tickets) {
                                    if (payload.tickets.tickets.length) {
                                        dispatchTicketReloadModal({
                                            type: TicketReloadModalActionType.Show,
                                            ticketId: payload.tickets.tickets[0].id,
                                        });
                                    }
                                } else if (payload.ticket) {
                                    dispatchTicketReloadModal({
                                        type: TicketReloadModalActionType.Show,
                                        ticketId: payload.ticket.id,
                                    });
                                }
                            } else if (fieldMapString === 'rem' || fieldMapString === 'closedDate') {
                                dispatch({
                                    type: MessageActionType.ShowError,
                                    text: map(detailErrors, 'message').join('\n'),
                                });
                            } else if (errorMessage) {
                                dispatch({
                                    type: MessageActionType.ShowError,
                                    text: errorMessage,
                                });
                            } else if (detailErrors.length) {
                                dispatch({
                                    type: MessageActionType.ShowError,
                                    text: map(detailErrors, 'message').join(', '),
                                });
                            }
                            break;

                        case Status.InternalError:
                        case Status.NotFound:
                            dispatch({
                                type: MessageActionType.ShowError,
                                text: t('PLATFORM_ERROR_MESSAGE'),
                            });
                            break;
                    }
                }
                return Promise.reject(error);
            },
        );
        setInterceptorRegistered(true);
    }, [profile, dispatch, t, dispatchTicketReloadModal]);

    return <>{isInterceptorRegistered ? children : null}</>;
};
